'use client'

import type { ClientOutputs, Workspace } from '@botpress/client'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@bpinternal/ui-kit-next'
import { type LinkOptions, useParams } from '@tanstack/react-router'
import {
  BookOpenText,
  Boxes,
  Bug,
  ChartPie,
  ChartSpline,
  ChevronsUpDown,
  CreditCard,
  House,
  LayoutPanelLeft,
  type LucideIcon,
  MessageSquareReply,
  Settings,
  Settings2,
} from 'lucide-react'
import type { ReactNode } from 'react'
import * as React from 'react'
import { Badge } from '~/elements'
import { NotificationBadge } from '../../../features/issues/componentsV2'
import { WorkspaceProfilePicture } from '../../../features/workspaces/components'
import { type FeatureToggle, useCurrentRouteId, useFeature, useWorkspaces } from '../../../hooks'
import { useQuery } from '../../../services'
import { useAppStore, useLocalStore } from '../../../stores'
import { Boundary } from '../../Boundary'
import { NavGroup } from '../NavGroup'
import { NavUsages } from '../NavUsages'
import { WorkspacesDropdown } from '../WorkspacesDropdown'
import './styles.css'

export type NavItem = {
  title: string
  linkOptions: LinkOptions
  icon?: LucideIcon
  isActive?: boolean
  content?: ReactNode
  hidden?: boolean
  subItems?: NavItem[]
}

/**
 * Given a routeId, will return a mapping function.
 *
 * The mapping function will return a new NavItem with the `isActive` property
 * based on the provided routeId.
 */
const withIsActive =
  (routeId?: string) =>
  (item: NavItem): NavItem => {
    const updatedSubItems = item.subItems?.map(withIsActive(routeId)) || []
    return {
      ...item,
      isActive: item.linkOptions.to && routeId?.includes(item.linkOptions.to),
      subItems: updatedSubItems,
    }
  }

const CreateWorkspaceNavItems: ({ workspaceId, routeId }: { workspaceId: string; routeId?: string }) => NavItem[] = ({
  routeId,
  ...params
}) =>
  (
    [
      {
        title: 'Home',
        icon: House,
        linkOptions: { params, to: '/workspaces/$workspaceId/home' },
      },
      {
        title: 'Integrations',
        icon: Boxes,
        linkOptions: { params, to: '/workspaces/$workspaceId/integrations' },
      },
      { title: 'Usage', icon: ChartPie, linkOptions: { params, to: '/workspaces/$workspaceId/usage' } },
      {
        title: 'Billing',
        icon: CreditCard,
        content: <NewTabBadge />,
        linkOptions: { params, to: '/workspaces/$workspaceId/billing' },
      },
      {
        title: 'Settings',
        icon: Settings,
        linkOptions: { params, to: '/workspaces/$workspaceId/settings/details' },
      },
    ] as const satisfies NavItem[]
  ).map(withIsActive(routeId))

const CreateBotNavItems: ({
  botId,
  workspaceId,
  routeId,
  enabledFeatures,
}: {
  botId: string
  workspaceId: string
  routeId?: string
  enabledFeatures?: Partial<Record<FeatureToggle, boolean>>
}) => NavItem[] = ({ routeId, enabledFeatures, ...params }) =>
  (
    [
      {
        title: 'Overview',
        icon: LayoutPanelLeft,
        linkOptions: { to: '/workspaces/$workspaceId/bots/$botId/overview', params },
      },
      {
        title: 'Monitor',
        icon: ChartSpline,
        linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/conversations' },
        subItems: [
          {
            title: 'Conversations',
            content: <NewTabBadge />,
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/conversations' },
          },
          {
            title: 'Users',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/users' },
          },
          {
            title: 'Analytics',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/analytics' },
          },
        ],
      },
      {
        title: 'Knowledge',
        icon: BookOpenText,
        linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/tables' },
        subItems: [
          {
            title: 'Tables',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/tables' },
          },
          {
            title: 'Knowledge Bases',
            content: <NewTabBadge />,
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/knowledge-bases' },
          },
        ],
      },

      {
        title: 'Inspect',
        icon: Bug,
        linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/logs' },
        subItems: [
          {
            title: 'Logs',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/logs' },
          },

          {
            title: 'Events',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/events' },
          },
          {
            title: 'Files',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/files' },
          },
          {
            title: 'Issues',
            content: (
              <Boundary loader={null} onError={() => null}>
                <NotificationBadge botId={params.botId} workspaceId={params.workspaceId} />
              </Boundary>
            ),

            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/issues' },
          },
          {
            title: 'Workflows',
            hidden: !enabledFeatures?.['workflow-page'],
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/workflows' },
          },
        ],
      },
      {
        title: 'Webchat',
        icon: Settings2,
        linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/webchat' },
        subItems: [
          {
            title: 'Bot Identity',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/webchat/v2/identity' },
          },
          {
            title: 'Bot Appearance',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/webchat/v2/appearance' },
          },
          {
            title: 'Deploy Settings',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/webchat/v2/deploy-settings' },
          },
          {
            title: 'Features',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/webchat/v2/features' },
          },
        ],
      },
      {
        title: 'Human Handoff',
        icon: MessageSquareReply,
        linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/hitl' },
        subItems: [
          {
            title: 'Dashboard',
            linkOptions: { params, to: '/workspaces/$workspaceId/bots/$botId/hitl' },
          },
        ],
      },
      {
        title: 'Settings',
        icon: Settings,
        linkOptions: {
          params,
          to: '/workspaces/$workspaceId/bots/$botId/settings/general',
        },
        subItems: [
          {
            title: 'General',
            linkOptions: {
              params,
              to: '/workspaces/$workspaceId/bots/$botId/settings/general',
            },
          },
          {
            title: 'Access Control',
            linkOptions: {
              params,
              to: '/workspaces/$workspaceId/bots/$botId/settings/access-control',
            },
          },
        ],
      },
    ] as const satisfies NavItem[]
  ).map(withIsActive(routeId))

type SidebarProps = React.ComponentProps<typeof Sidebar> & {
  workspaces: Workspace[]
}

export function AppSidebar({ workspaces, ...props }: SidebarProps) {
  const activeWorkspaceId = useAppStore((s) => s.activeWorkspace)?.id
  const activeWorkspace = useWorkspaces().data?.find((w) => w.id === activeWorkspaceId)

  const routeId = useCurrentRouteId()

  const { workspaceId, botId } = useParams({ strict: false })
  const lastBotId = useLocalStore((state) => state.getLastActiveBotId(workspaceId))
  const navBotId = botId ?? lastBotId

  const workflowsEnabled = useFeature('workflow-page')

  const { data: bot } = useQuery('workspaces_/$workspaceId_/bots_/$botId_', {
    workspaceId: workspaceId ?? '',
    botId: navBotId ?? '',
  })

  const isInBotContext = routeId?.includes('/bots/') && routeId !== '/workspaces/$workspaceId/bots/$botId/new-bot'

  const getPlanLabel = (plan: ClientOutputs['getWorkspace']['plan']) => (plan === 'community' ? 'Pay-as-you-go' : plan)

  return (
    <Sidebar collapsible="icon" variant="inset" {...props} className="p-0">
      {activeWorkspace && (
        <SidebarHeader className="pr-0">
          <SidebarMenu>
            <SidebarMenuItem className="flex h-14 items-center">
              <WorkspacesDropdown workspaces={workspaces}>
                <SidebarMenuButton size="lg">
                  <WorkspaceProfilePicture
                    workspaceId={activeWorkspace.id}
                    className="size-8 flex-none rounded-md"
                    pictureUrl={activeWorkspace.profilePicture}
                  />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{activeWorkspace.name}</span>
                    <span className="truncate text-xs first-letter:uppercase">
                      {getPlanLabel(activeWorkspace.plan)}
                    </span>
                  </div>
                  <ChevronsUpDown className="!h-5 !w-5 text-muted-foreground" />
                </SidebarMenuButton>
              </WorkspacesDropdown>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
      )}
      <SidebarContent className="p-0">
        {isInBotContext ? (
          <NavGroup
            items={
              bot
                ? CreateBotNavItems({
                    botId: bot.id,
                    workspaceId: workspaceId ?? '',
                    routeId,
                    enabledFeatures: { 'workflow-page': workflowsEnabled },
                  })
                : []
            }
          />
        ) : (
          <NavGroup
            items={CreateWorkspaceNavItems({
              workspaceId: workspaceId ?? '',
              routeId,
            })}
          />
        )}
      </SidebarContent>

      {workspaceId && (
        <SidebarFooter className="pr-0">
          <Boundary loader={null} onError={() => null}>
            <NavUsages workspaceId={workspaceId} />
          </Boundary>
        </SidebarFooter>
      )}
    </Sidebar>
  )
}

/**
 * @internal
 * Sidebar-specific variant of the design-system Badge.
 * This intentionally tweaks spacing/typography to better fit the AppSidebar UI
 * and is not meant to be reused elsewhere.
 */
const NewTabBadge: React.FC = () => {
  return (
    <Badge variant="secondary" color="blue" className="py-0 text-[10px] leading-4">
      New
    </Badge>
  )
}
